<template>
  <div v-if="!error" class="flex-row bg-white py-6 lg:flex">
    <Headline
      inline
      tag="h2"
      look="h3"
      class="flex px-8 pb-6 lg:shrink-0 lg:flex-col lg:place-content-center lg:py-6"
    >
      {{ $t('vto.vto_recommendations_title') }}
    </Headline>
    <div class="flex-1 overflow-x-auto">
      <HorizontalItemsScroller
        :start-at-index="0"
        :has-gradient="false"
        show-buttons
      >
        <ScrollerItem class="flex gap-6 px-8">
          <template v-if="fetching">
            <Skeleton
              v-for="n in LOADING_PLACEHOLDERS"
              :key="n"
              class="h-28 w-44 shrink-0 rounded-md p-3"
              type="custom"
            />
          </template>
          <template v-else-if="!error">
            <div
              v-for="item in similarItems"
              :key="item.sku"
              class="h-28 w-44 shrink-0 cursor-pointer rounded-md bg-shade-200 p-3"
              @click="handleItemClick(item)"
            >
              <div class="relative size-full">
                <Skeleton
                  v-if="loadingStates[item.sku]"
                  class="absolute inset-0 size-full"
                  type="custom"
                />
                <ProductImage
                  :image="{ hash: item.imageUrl }"
                  :alt="item.description"
                  fit="cover"
                  :load="() => handleImageLoad(item.sku)"
                />
              </div>
            </div>
          </template>
        </ScrollerItem>
      </HorizontalItemsScroller>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SyteSimilarItem } from '~/api/syteClient'

const { trackRadioButtonClick } = await useTrackingEvents()

const props = defineProps({
  variantId: {
    type: Number as PropType<number>,
    required: true,
  },
})

const emit = defineEmits<{
  (e: 'item-click', productId: number): void
}>()

const variantId = toRef(props, 'variantId')

const { data, error, fetching } = useSyteSimilarItems(variantId, {
  immediate: true,
})

const similarItems = computed(() => data?.value?.response ?? [])

const LOADING_PLACEHOLDERS = 10

// Add this to track loading state for each image
const loadingStates = ref<Record<string, boolean>>({})

// Initialize loading states when items change
watchEffect(() => {
  similarItems.value.forEach((item) => {
    if (!loadingStates.value[item.sku]) {
      loadingStates.value[item.sku] = true
    }
  })
})

// Handle image load completion
const handleImageLoad = (sku: string) => {
  loadingStates.value[sku] = false
}

const handleItemClick = (item: SyteSimilarItem) => {
  trackRadioButtonClick({ id: 'radio_frame-change', label: item.description })
  emit('item-click', Number(item.color_sku))
}
</script>
